import React, { useCallback, useContext, useEffect, useState } from 'react'
import { AuthContext } from '../../../../context/auth/auth-context'
import axios from 'axios'
import dot from 'dot-object'
import { useForm } from 'react-hook-form'
import { type AlertColor, Button, Modal, Stack, Typography, useTheme, Grid, Card, CardContent, IconButton, Chip, Box } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import RefreshIcon from '@mui/icons-material/Refresh'
import SaveIcon from '@mui/icons-material/Save'
import { ExtendedPromise } from '../../../../utils/promises-utils'
import { formatToLocalMilliTimestampFromUTCNumber } from '../../../../utils/time-utils'
import { formAlertErrorMessage, handleRaceSettleAllResponse } from '../../../../utils/string-utils'
import ConfirmationModal from '../../../../components/modal/confirmation-modal'
import ControlledTextField from '../../../../components/inputs/textfield/controlled-text-field'
import ToastWithMessage from '../../../../components/toast/toast-with-message'
import ViewJSONTrigger from '../view/view-json-trigger'

interface ConstraintsModalProps {
  open: boolean
  onClose: () => void
  strategyId: string
  title: string
  dataUrl: string | null
  postUrl: string | null
}

const VENUE_LOGICAL_SORT_ORDER = ['sbn', 'fbnu', 'fbnc', 'okx', 'spbit', 'swbit', 'dbit', 'bybit']

const ConstraintsModal: React.FC<ConstraintsModalProps> = (
  { open, onClose, strategyId, title, dataUrl, postUrl }
) => {
  const theme = useTheme()
  const [data, setData] = useState<Record<string, Record<string, string>>>({})
  const [retrievalTimestamp, setRetrievalTimestamp] = useState<number>(0)

  const { isAuthorized, accessToken } = useContext(AuthContext)

  const getConstraints = useCallback(() => {
    if (dataUrl != null && dataUrl.length !== 0) {
      axios
        .get(dataUrl, { headers: { Authorization: `Bearer ${accessToken}` } })
        .then(response => {
          setData(dot.object(response.data) as Record<string, Record<string, string>>)
          setRetrievalTimestamp(Date.now())
        })
        .catch(reason => {
          console.error(reason)
          setData({})
          setRetrievalTimestamp(-1)
        })
    }
  }, [isAuthorized, accessToken, dataUrl])

  useEffect(() => {
    if (open && isAuthorized) {
      getConstraints()
    }
  }, [open, isAuthorized])

  useEffect(() => {
    // defaultValues are cached on the first render, so we must reset on state change
    reset(data, { keepDirty: false, keepDirtyValues: false })
  }, [data])

  const { handleSubmit, reset, control, formState } = useForm<Record<string, Record<string, string>>>({
    defaultValues: data
  })
  const handleUpdate = useCallback((update: Record<string, Record<string, string>>) => {
    if (postUrl == null || postUrl.length === 0) {
      console.debug('[Constraints Modal] Post url is empty')
      displayToastWithMessage('The POST url is invalid - please check the related configuration again', 'warning')
      return
    }

    // Flatten into the format of: constraintGroupKey.constraintKey
    const dotFormChangedFields = dot.dot(formState.dirtyFields)
    if (Object.keys(dotFormChangedFields).length === 0) {
      console.debug('[Constraints Modal] No parameters were changed')
      displayToastWithMessage('There are no parameter changes to be updated', 'warning')
      return
    }

    const dotFormChangedFieldData = dot.dot(update)

    const promises = Object.keys(dotFormChangedFields).map(async changedField => {
      const newValue = dotFormChangedFieldData[changedField]
      const message = {
        timestamp: new Date().toISOString(),
        name: changedField,
        value: newValue,
      }

      console.debug(`[Constraints Modal] Queuing update request for the constraint: ${changedField}`)
      console.debug(message)

      return await axios.post(postUrl, message, { headers: { Authorization: `Bearer ${accessToken}` } })
    })

    ExtendedPromise
      .raceSettleAll(promises, 10000, new Error('timeout'))
      .then(response => {
        console.debug(response)
        handleRaceSettleAllResponse(response, true, displayToastWithMessage)
      })
      .catch(error => {
        console.error(error)
        displayToastWithMessage(formAlertErrorMessage(error), 'error')
      })
  }, [postUrl, formState.dirtyFields, accessToken])

  const [toastOpen, setToastOpen] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastSeverity, setToastSeverity] = useState<AlertColor>('info')
  const displayToastWithMessage = useCallback((message: string, severity: AlertColor): void => {
    setToastMessage(message)
    setToastSeverity(severity)
    setToastOpen(true)
  }, [strategyId])

  const [modalOpen, setModalOpen] = useState<boolean>(false)
  const openModal = (): void => { setModalOpen(true) }
  const closeModal = (): void => {
    setModalOpen(false)
  }

  const onModalClose = useCallback(() => {
    setToastOpen(false)
    onClose()
  }, [onClose])

  return (
    <Modal
      open={open}
      onClose={onModalClose}
      aria-labelledby='modal-modal-title'
      aria-describedby='modal-modal-description'
    >
      <Stack
        sx={{
          position: 'absolute' as 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '90%',
          maxWidth: '1600px',
          height: '80%',
          maxHeight: '1000px',
          bgcolor: 'background.paper',
          border: `1px solid ${theme.palette.primary.main}`,
          boxShadow: 24,
          p: 4,
          color: 'white',
          overflowY: 'auto',
        }}
        gap={2}
      >
        <Stack direction='row' alignItems='center' gap={1}>
          <Typography id='modal-modal-title' fontWeight='bold' variant='h5' component='h1'>
            {title}
          </Typography>
          <Chip
            label={formatToLocalMilliTimestampFromUTCNumber(retrievalTimestamp)}
            size='small'
          />
          <ViewJSONTrigger dataUrl={dataUrl}/>
          <Box marginX='auto'></Box>
          <IconButton color='error' onClick={onModalClose}>
            <CloseIcon/>
          </IconButton>
        </Stack>

        <Stack direction='row' gap={8}>
          <Button variant='outlined' sx={{ width: 'fit-content' }} startIcon={<RefreshIcon/>} onClick={getConstraints}>
            Reload
          </Button>
          <Button variant='outlined' startIcon={<SaveIcon/>} onClick={openModal}>
            Update
          </Button>
        </Stack>

        <form
          id={'constraints-form'}
          /* eslint-disable-next-line @typescript-eslint/no-misused-promises */
          onSubmit={handleSubmit(handleUpdate)}
          noValidate
        >
          <Grid container spacing={2}>
            {
              Object.entries(data)
                .sort(([key1], [key2]) => key1.localeCompare(key2, undefined, { numeric: true }))
                .map(([constraintGroupKey, mapValue]) => {
                  const children =
                    Object.keys(mapValue)
                      .sort((a, b) => {
                        if (VENUE_LOGICAL_SORT_ORDER.includes(a) && VENUE_LOGICAL_SORT_ORDER.includes(b)) {
                          return VENUE_LOGICAL_SORT_ORDER.indexOf(a) - VENUE_LOGICAL_SORT_ORDER.indexOf(b)
                        } else {
                          return a.localeCompare(b, undefined, { numeric: true })
                        }
                      })
                      .map(constraintKey => {
                        return (
                          <ControlledTextField
                            key={`${constraintGroupKey}.${constraintKey}`}
                            control={control}
                            name={`${constraintGroupKey}.${constraintKey}`}
                            fieldId={`${constraintGroupKey}.${constraintKey}`}
                            label={constraintKey}
                            value={data[constraintGroupKey][constraintKey]}
                          />
                        )
                      })

                  return (
                    <Grid key={constraintGroupKey} item xs={12} md={6} lg={3}>
                      <Typography color='primary' fontSize='large' fontWeight='bold'>{constraintGroupKey}</Typography>
                      <Card variant='outlined'>
                        <CardContent>
                          <Stack gap={1}>
                            {children}
                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>
                  )
                })
            }
          </Grid>
        </form>

        <ConfirmationModal
          open={modalOpen}
          onClose={closeModal}
          onHandle={handleSubmit(handleUpdate)}
          title={'Update constraint?'}
          description={'Note: This will update the constraint value for the current strategy session only. This value will NOT persist after the strategy is shutdown.'}
          handleButtonLabel={'Update constraint'}
          handleButtonColour={'primary'}
        />

        <ToastWithMessage toastOpen={toastOpen} setToastOpen={setToastOpen} message={toastMessage} severity={toastSeverity}/>
      </Stack>
    </Modal>
  )
}

export default ConstraintsModal
